import {
  RiAwardLine,
  RiCalendarScheduleLine,
  RiClipboardLine,
  RiHistoryLine,
  RiPulseLine,
  RiUserFollowLine,
} from '@remixicon/react';
import { addMinutes } from 'date-fns';
import { useMemo } from 'react';

import type { GetAllReservations } from '@virtual-care/client';
import { displayName, formatDateTimeShort } from '@virtual-care/isomorphic';

type Reservation = GetAllReservations['records'][number];
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@virtual-care/shadcn';
import { ReservationStatus, useData } from '@virtual-care/ui';

function InlineLoader({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="text-muted-foreground flex items-center gap-2 text-sm">
      <div className="border-t-primary h-4 w-4 animate-spin rounded-full border-2 border-gray-200" />
      {label}
    </div>
  );
}

function CountCard({
  title,
  value,
  icon,
}: {
  title: string;
  value: number | undefined;
  icon: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold">{value ?? '—'}</div>
      </CardContent>
    </Card>
  );
}

function QueueSection({
  title,
  status,
}: {
  title: string;
  status:
    | 'WAITING_DOCTOR_ASSIGNMENT'
    | 'WAITING_DOCTOR_CONFIRMATION'
    | 'AVAILABLE_FOR_FOLLOWUP';
}) {
  const { data, isLoading } = useData('GET /reservations', {
    status,
    page: 1,
    pageSize: 8,
  });

  const records = (data?.records || []) as Reservation[];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <InlineLoader />
        ) : records.length === 0 ? (
          <div className="text-muted-foreground text-sm">No items.</div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-left">Reservation</TableHead>
                  <TableHead className="text-left">Patient</TableHead>
                  <TableHead className="text-left">Speciality</TableHead>
                  <TableHead className="text-left">Service</TableHead>
                  <TableHead className="text-left">When</TableHead>
                  <TableHead className="text-left">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((it) => {
                  const start = new Date(it.scheduledDate);

                  return (
                    <TableRow key={it.id}>
                      <TableCell className="text-left">
                        #{it.reservationNumber}
                      </TableCell>
                      <TableCell className="text-left">
                        {displayName(it.user)}
                      </TableCell>
                      <TableCell className="text-left">
                        {it.speciality?.name ?? '—'}
                      </TableCell>
                      <TableCell className="text-left">
                        {formatDateTimeShort(start)}
                      </TableCell>
                      <TableCell className="text-left">
                        <ReservationStatus.Root
                          status={it.status}
                          className="rounded-full"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function UpcomingAppointments() {
  // Combine multiple statuses to approximate the upcoming schedule
  const { data: confirmed, isLoading: l1 } = useData('GET /reservations', {
    status: 'CONFIRMED',
    page: 1,
    pageSize: 10,
  });
  const { data: waitingConfirm, isLoading: l2 } = useData('GET /reservations', {
    status: 'WAITING_DOCTOR_CONFIRMATION',
    page: 1,
    pageSize: 10,
  });
  const { data: waitingAssign, isLoading: l3 } = useData('GET /reservations', {
    status: 'WAITING_DOCTOR_ASSIGNMENT',
    page: 1,
    pageSize: 10,
  });

  const loading = l1 || l2 || l3;
  const merged = useMemo(() => {
    const a = (confirmed?.records || []) as Reservation[];
    const b = (waitingConfirm?.records || []) as Reservation[];
    const c = (waitingAssign?.records || []) as Reservation[];
    return [...a, ...b, ...c]
      .sort(
        (x, y) =>
          new Date(x.scheduledDate).getTime() -
          new Date(y.scheduledDate).getTime(),
      )
      .slice(0, 10);
  }, [confirmed, waitingConfirm, waitingAssign]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upcoming appointments</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <InlineLoader />
        ) : merged.length === 0 ? (
          <div className="text-muted-foreground text-sm">
            No upcoming items.
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-left">Reservation</TableHead>
                  <TableHead className="text-left">Patient</TableHead>
                  <TableHead className="text-left">Doctor</TableHead>
                  <TableHead className="text-left">Speciality</TableHead>
                  <TableHead className="text-left">When</TableHead>
                  <TableHead className="text-left">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {merged.map((it) => {
                  const start = new Date(it.scheduledDate);

                  const doctorName =
                    `${it.doctor?.firstName ?? ''} ${it.doctor?.lastName ?? ''}`.trim() ||
                    '—';
                  return (
                    <TableRow key={it.id}>
                      <TableCell className="text-left">
                        #{it.reservationNumber}
                      </TableCell>
                      <TableCell className="text-left">
                        {displayName(it.user)}
                      </TableCell>
                      <TableCell className="text-left">{doctorName}</TableCell>
                      <TableCell className="text-left">
                        {it.speciality?.name ?? '—'}
                      </TableCell>
                      <TableCell className="text-left">
                        {formatDateTimeShort(start)}
                      </TableCell>
                      <TableCell className="text-left">
                        <ReservationStatus.Root
                          status={it.status}
                          className="rounded-full"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RecentReservations() {
  const { data, isLoading } = useData('GET /reservations', {
    page: 1,
    pageSize: 6,
  });
  const records = (data?.records || []) as Reservation[];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RiHistoryLine className="h-4 w-4" /> Recent reservations
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <InlineLoader />
        ) : records.length === 0 ? (
          <div className="text-muted-foreground text-sm">
            No recent reservations.
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-left">Reservation</TableHead>
                  <TableHead className="text-left">Patient</TableHead>
                  <TableHead className="text-left">Speciality</TableHead>
                  <TableHead className="text-left">Created</TableHead>
                  <TableHead className="text-left">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((it) => {
                  return (
                    <TableRow key={it.id}>
                      <TableCell className="text-left">
                        #{it.reservationNumber}
                      </TableCell>
                      <TableCell className="text-left">
                        {displayName(it.user)}
                      </TableCell>
                      <TableCell className="text-left">
                        {it.speciality?.name ?? '—'}
                      </TableCell>
                      <TableCell className="text-left">
                        {formatDateTimeShort(new Date(it.createdAt))}
                      </TableCell>
                      <TableCell className="text-left">
                        <ReservationStatus.Root
                          status={it.status}
                          className="rounded-full"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SpecialityCapacity() {
  // Show first N specialities and their available slots for today
  const { data, isLoading } = useData('GET /specialities', {
    page: 1,
    pageSize: 12,
    sortBy: 'name',
    sortOrder: 'asc',
  });
  const specs = data?.records || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RiPulseLine className="h-4 w-4" /> Today capacity snapshot
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <InlineLoader />
        ) : specs.length === 0 ? (
          <div className="text-muted-foreground text-sm">No specialities.</div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {specs.map((s: any) => (
              <SpecialityAvailabilityCard key={s.id} id={s.id} name={s.name} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SpecialityAvailabilityCard({
  id,
  name,
}: {
  id: string;
  name: string;
}) {
  // Compute a slightly future timestamp once (per mount) to satisfy server validation and avoid clock-skew
  const date = useMemo(() => addMinutes(new Date(), 31).toISOString(), []);
  const { data, isLoading } = useData(
    'GET /availability/speciality/{specialityId}/slots',
    {
      specialityId: id,
      date,
    },
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retry: false,
    },
  );
  const availableCount = (data || []).length;

  return (
    <div className="rounded-lg border p-3">
      <div className="flex items-center justify-between">
        <div className="font-semibold">{name}</div>
        <RiCalendarScheduleLine className="text-muted-foreground h-4 w-4" />
      </div>
      <Separator className="my-2" />
      {isLoading ? (
        <InlineLoader />
      ) : (
        <div className="text-sm">
          <span className="font-medium">Available slots today:</span>{' '}
          {availableCount}
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const { data: dAssign } = useData('GET /reservations', {
    status: 'WAITING_DOCTOR_ASSIGNMENT',
    page: 1,
    pageSize: 1,
  });
  const { data: dConfirm } = useData('GET /reservations', {
    status: 'WAITING_DOCTOR_CONFIRMATION',
    page: 1,
    pageSize: 1,
  });
  const { data: dFollow } = useData('GET /reservations', {
    status: 'AVAILABLE_FOR_FOLLOWUP',
    page: 1,
    pageSize: 1,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground text-sm">
          Morning snapshot for coordinator
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <CountCard
          title="Need assignment"
          value={dAssign?.totalCount}
          icon={<RiClipboardLine className="text-muted-foreground h-4 w-4" />}
        />
        <CountCard
          title="Waiting doctor confirmation"
          value={dConfirm?.totalCount}
          icon={<RiUserFollowLine className="text-muted-foreground h-4 w-4" />}
        />
        <CountCard
          title="Follow-ups available"
          value={dFollow?.totalCount}
          icon={<RiAwardLine className="text-muted-foreground h-4 w-4" />}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <UpcomingAppointments />
        </div>
        <div className="space-y-4">
          <RecentReservations />
        </div>
      </div>
      <SpecialityCapacity />

      <div className="grid gap-4 lg:grid-cols-3">
        <QueueSection
          title="Queue: Need assignment"
          status="WAITING_DOCTOR_ASSIGNMENT"
        />
        <QueueSection
          title="Queue: Waiting confirmation"
          status="WAITING_DOCTOR_CONFIRMATION"
        />
        <QueueSection
          title="Queue: Follow-up available"
          status="AVAILABLE_FOR_FOLLOWUP"
        />
      </div>
    </div>
  );
}
